/**
 * Route Guard Component for Domain-Based Access Control
 * 
 * This component wraps routes to enforce domain-based access control.
 * - Admin routes only accessible from admin.blackmatte.fr
 * - Public routes only accessible from blackmatte.fr
 * - Development environments allow all routes
 */

import React, { useEffect, useState } from 'react';
import Seo from './Seo';
import { seo } from '../seo/seoData';
import { useLocation, useNavigate } from 'react-router-dom';
import { isRouteAccessible, getRedirectUrl, getDomainInfo, isDevelopment } from '../utils/domainAccess';

/**
 * Route Guard Component
 * Checks if the current route is accessible on the current domain.
 * Redirects or shows error if access is blocked.
 */
const RouteGuard = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAllowed, setIsAllowed] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  
  useEffect(() => {
    const checkAccess = () => {
      const accessible = isRouteAccessible(location.pathname);
      
      if (!accessible) {
        const redirectUrl = getRedirectUrl(location.pathname);
        
        if (redirectUrl) {
          // Check if it's an external redirect
          if (redirectUrl.startsWith('http')) {
            setRedirecting(true);
            window.location.href = redirectUrl;
          } else {
            // Internal redirect
            navigate(redirectUrl, { replace: true });
          }
        } else {
          setIsAllowed(false);
        }
      } else {
        setIsAllowed(true);
      }
    };
    
    checkAccess();
  }, [location.pathname, navigate]);
  
  // Show loading while redirecting externally
  if (redirecting) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-neutral-400">Redirection en cours...</p>
        </div>
      </div>
    );
  }
  
  // Show access denied message
  if (!isAllowed) {
    return <AccessDeniedPage />;
  }
  
  return children;
};

/**
 * Access Denied Page Component
 */
const AccessDeniedPage = () => {
  const domainInfo = getDomainInfo();
  
  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg 
            className="w-8 h-8 text-red-500" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-2">Accès Refusé</h1>
        <p className="text-neutral-400 mb-6">
          Cette page n'est pas accessible depuis ce domaine.
        </p>
        
        <div className="space-y-3">
          <a 
            href="https://blackmatte.fr" 
            className="block w-full py-3 px-4 bg-white/10 hover:bg-white/15 text-white rounded-xl transition-colors"
          >
            Retourner au site public
          </a>
          <a 
            href="https://admin.blackmatte.fr" 
            className="block w-full py-3 px-4 bg-white/5 hover:bg-white/10 text-neutral-400 rounded-xl transition-colors"
          >
            Accéder à l'administration
          </a>
        </div>
        
        {/* Debug info in development */}
        {isDevelopment() && (
          <div className="mt-8 p-4 bg-neutral-900 rounded-xl text-left">
            <p className="text-xs text-neutral-500 font-mono mb-2">Debug Info:</p>
            <pre className="text-xs text-neutral-400 font-mono overflow-auto">
              {JSON.stringify(domainInfo, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Admin Route Wrapper
 * Use this to wrap admin-only routes for additional protection
 */
export const AdminRoute = ({ children }) => {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  
  useEffect(() => {
    // In development, allow all
    if (isDevelopment()) {
      setAllowed(true);
      setChecking(false);
      return;
    }
    
    // In production, check domain
    const domainInfo = getDomainInfo();
    if (domainInfo.isAdminDomain) {
      setAllowed(true);
    } else {
      // Redirect to admin subdomain
      window.location.href = `https://admin.blackmatte.fr${location.pathname}`;
    }
    setChecking(false);
  }, [location.pathname]);

  if (checking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return allowed ? (
    <>
      <Seo {...seo.admin} />
      {children}
    </>
  ) : null;
};

/**
 * Public Route Wrapper
 * Use this to wrap public-only routes for additional protection
 */
export const PublicRoute = ({ children }) => {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  
  useEffect(() => {
    // In development, allow all
    if (isDevelopment()) {
      setAllowed(true);
      setChecking(false);
      return;
    }
    
    // In production, check domain
    const domainInfo = getDomainInfo();
    if (domainInfo.isPublicDomain || domainInfo.isDevelopment) {
      setAllowed(true);
    } else {
      // Redirect to public domain
      window.location.href = `https://blackmatte.fr${location.pathname}`;
    }
    setChecking(false);
  }, [location.pathname]);
  
  if (checking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return allowed ? children : null;
};

export default RouteGuard;
